import { useInfiniteQuery } from "@tanstack/react-query"
import { client } from "@/api"
import type { ProcessingStage, StageStatus } from "@/hooks/useClips"

// One row from GET /admin/processing-events: a single clip_processing_events entry joined
// to its clip/game/team for context. Internal numeric ids are omitted server-side
// (Invariant #1). `trigger` is pulled out of `detail` server-side for convenience; `detail`
// is still returned for the hover.
export interface ProcessingEvent {
  stage: ProcessingStage
  status: StageStatus
  attempt: number
  error: string | null
  duration_ms: number | null
  detail: Record<string, unknown> | null
  created_at: string
  trigger: string | null
  // Server-computed: true only when this row is the current, non-terminal, over-threshold
  // state for its (clip, stage). The single source of truth for the stuck badge/count, so a
  // superseded old running/pending row is never false-flagged.
  is_stuck: boolean
  clip_public_id: string
  game_public_id: string | null
  team_public_id: string | null
  team_name: string | null
}

interface ProcessingEventsResponse {
  events: ProcessingEvent[]
  stuckThresholdMinutes: number
}

export type ProcessingStageFilter = "all" | ProcessingStage
export type ProcessingStatusFilter = "all" | StageStatus

// Backend caps `limit` at 100; a page of 50 keeps the live poll cheap while still filling
// the screen.
export const PROCESSING_EVENTS_PAGE_SIZE = 50

interface UseProcessingEventsOptions {
  stage?: ProcessingStageFilter
  status?: ProcessingStatusFilter
  stuck?: boolean
}

export function useProcessingEvents({
  stage = "all",
  status = "all",
  stuck = false,
}: UseProcessingEventsOptions = {}) {
  return useInfiniteQuery<ProcessingEventsResponse>({
    queryKey: ["admin", "processing-events", { stage, status, stuck }],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const offset = pageParam as number
      const response = await client.admin["processing-events"].$get({
        query: {
          limit: PROCESSING_EVENTS_PAGE_SIZE.toString(),
          offset: offset.toString(),
          ...(stage !== "all" ? { stage } : {}),
          // The status filter is a no-op in stuck mode (status is pinned to running/pending
          // server-side), so don't send it then.
          ...(status !== "all" && !stuck ? { status } : {}),
          ...(stuck ? { stuck: "true" as const } : {}),
        },
      })
      if (!response.ok) {
        throw new Error(`Failed to fetch processing events: ${response.status}`)
      }
      return response.json() as Promise<ProcessingEventsResponse>
    },
    // A short page means the end; otherwise the next offset is the count loaded so far.
    getNextPageParam: (lastPage, allPages) =>
      lastPage.events.length < PROCESSING_EVENTS_PAGE_SIZE
        ? undefined
        : allPages.reduce((n, p) => n + p.events.length, 0),
    // Live monitoring view: poll every 5s. useInfiniteQuery refetches all loaded pages, so
    // page one (the newest activity) stays current. No refetchIntervalInBackground
    // (2026-09-28 incident): installIdlePause (@playside/tab-activity) already pauses
    // this while the tab is hidden or idle and resumes it on the next activity.
    refetchInterval: 5000,
  })
}
